import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, of, switchMap, tap } from 'rxjs';
import { SapError } from 'src/app/lego-utils';
import { EquipmentDetails } from 'src/app/models/domain/equipment-details';
import { EquipmentItemDataService } from 'src/app/services';
import { EquipmentLookupService } from 'src/app/state/equipment/equipment-lookup.service';

@Injectable({ providedIn: 'root' })
export class EquipmentDetailsService {
  private readonly _details = new BehaviorSubject<EquipmentDetails | undefined>(undefined);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<unknown>(undefined);

  public readonly details$ = this._details.asObservable();
  public readonly loading$ = this._loading.asObservable();
  public readonly error$ = this._error.asObservable();

  public constructor(
    private readonly equipmentLookup: EquipmentLookupService,
    private readonly equipmentDataService: EquipmentItemDataService
  ) {
    this.equipmentLookup.selectedEquipmentId$
      .pipe(
        filter((id): id is string => !!id),
        tap(() => {
          this._loading.next(true);
          this._error.next(undefined); // Clear error on new request
        }),
        switchMap((id) =>
          this.equipmentDataService.getEquipmentsByEquipmentId(id).pipe(
            tap((details) => {
              this._details.next(details);
            }),
            catchError((err) => {
              this._details.next(undefined);

              if (err instanceof SapError && err.httpStatusCode !== 404) {
                this._error.next(err);
              }

              return of(undefined);
            }),
            finalize(() => {
              this._loading.next(false);
            })
          )
        )
      )
      .subscribe();
  }

  public clear(): void {
    this._details.next(undefined);
    this._error.next(undefined);
  }
}
